import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TokenStorageService } from '../../../services/token.storage.service';
import {UserRole} from '../../../model/role.enum';
import {SortableTableComponent} from '../../elements/sortable-table/sortable-table.component';
import {Router} from '@angular/router';
import {IconComponent} from '../../elements/icon/icon.component';
import {Icons} from '../../../model/icon';
import {LoginService} from '../../../services/login.service';
import {DocumentService} from '../../../services/document-service';
import {Pagination, PaginationParams} from '../../../model/pagination';
import {PaginationComponent} from '../../elements/pagination/pagination.component';
import {Header} from '../../../model/header';
import {LoadingComponent} from '../../elements/loading/loading.component';
import {AdminStorageService} from '../../../services/admin.storage.service';
import {AdminSettings, DocumentsSettings} from '../../../model/adminSettings';
import {FormsModule} from '@angular/forms';
import {scrollUp} from '../../../functions/scroll-up';

@Component({
  selector: 'app-vouchers-page',
  imports: [CommonModule, SortableTableComponent, IconComponent, PaginationComponent, LoadingComponent, FormsModule],
  templateUrl: './vouchers-page.component.html',
  standalone: true,
  styleUrl: './vouchers-page.component.scss'
})
export class VouchersPageComponent implements OnInit, OnDestroy {
  constructor(protected tokenStorageService: TokenStorageService,
              private router: Router,
              protected loginService: LoginService,
              private documentService: DocumentService,
              protected adminStorageService: AdminStorageService) {}

  private tokenCheckInterval?: any;

  currentUser: string | null = null;
  userRole: string = '';
  search: string = '';

  isTab: boolean = false;
  isMobile: boolean = false;
  mobileMenuOpen: boolean = false;

  defaultParams = new PaginationParams({
    sort: '',
    query: '',
    pageSize: 5,
    pageNumber: 0,
  });
  params: PaginationParams = new PaginationParams(this.defaultParams);
  pagination: Pagination<any> = {page: {number: 0, totalPages: 0, size: 0, totalElements: 0}, content: []};
  loading: boolean = false;

  header: Header[] = [];
  currentSort: string = '';
  currentSortHeader: string = '';
  getClearSorting: number = 0;
  mouseLogout: boolean = false;

  settings: DocumentsSettings = new DocumentsSettings();

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isTab = window.innerWidth <= 769;
    this.isMobile = window.innerWidth <= 521;
  }

  ngOnInit(): void {
    this.loading = true;
    this.checkScreenSize();
    this.initializeComponent();
    this.startTokenMonitoring();
  }

  ngOnDestroy(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }

  private initializeComponent(): void {
    this.currentUser = this.tokenStorageService.getUsername();
    this.userRole = this.tokenStorageService.getPrimaryRole();
    if (!this.tokenStorageService.canAccessVouchers()) {
      return;
    }
    this.settings = this.adminStorageService.getDocumentsSetting();
    this.params = this.settings.paginationParams || new PaginationParams(this.defaultParams);
    this.currentSort = this.settings.currentSort || '';
    this.currentSortHeader = this.settings.currentSortHeader || '';
    this.search = this.settings.search || '';
    this.loadVouchers();
  }

  // В методе loadVouchers обновим формирование column
  private loadVouchers(): void {
    this.loading = true;
    this.documentService.getDocumentsList(this.params).subscribe((data) => {
      this.pagination = data;
      if (!this.isMobile) {
        this.pagination.content?.forEach((p) => {
          const column = `hotels: ${p.hotels.length}
        departures: ${p.departures.length};
        transits: ${p.transits.length};
        arrivals: ${p.arrivals.length};
        transfers: ${p.transfers.length};`
          p.column = column
        })
        this.header = [
          {
            title: 'id',
            key: 'id',
            width: "80px",
            sortable: true
          },
          {
            title: 'document type',
            key: 'type',
            width: "120px",
            sortable: true
          },
          {
            title: 'content',
            key: 'column',
            sortable: false,
            width: "auto"
          },
          {
            title: '',
            key: '',
            edit: true,
            width: "60px",
            sortable: false
          },
        ]
      } else {
        this.pagination.content?.forEach((p) => {
          const column = `hotels: ${p.hotels.length}
        departures: ${p.departures.length};
        transits: ${p.transits.length};
        arrivals: ${p.arrivals.length};
        transfers: ${p.transfers.length};`
          p.column = column
        })
        this.header = [
          {
            title: 'id',
            key: 'id',
            width: "80px",
            sortable: true
          },
          {
            title: 'document type',
            key: 'type',
            width: "120px",
            sortable: true
          },
          {
            title: 'content',
            key: 'column',
            sortable: false,
            width: "auto"
          },
        ]
      }

      this.loading = false;
    })
  }

  private startTokenMonitoring(): void {
    this.tokenCheckInterval = setInterval(() => {
      if (!this.tokenStorageService.isTokenValid()) {
        this.tokenStorageService.logout();
      }
    }, 30000);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  openItem(id?: number) {
    this.mobileMenuOpen = false;
    let url: string;
    if (id !== undefined) {
      url = this.router.createUrlTree(['/documents', id]).toString();
    } else {
      url = this.router.createUrlTree(['/documents/add']).toString();
    }
    scrollUp();
    this.router.navigate([url]).then();
  }

  navigateAdmin() {
    this.mobileMenuOpen = false;
    scrollUp();
    this.router.navigate( [ '/admin' ] ).then();
  }

  sorting(sort: string) {
    this.params.sort = sort;
    this.params.pageNumber = 0;
    this.settings.paginationParams = this.params;
    this.settings.currentSort = this.currentSort;
    this.settings.currentSortHeader = this.currentSortHeader;
    this.adminStorageService.setDocumentsSetting(this.settings);
    this.loadVouchers();
  }

  setCurrentSort(value: string) {
    this.currentSort = value;
    this.settings.currentSort = value;
    this.adminStorageService.setDocumentsSetting(this.settings);
  }

  setCurrentSortHeader(value: string) {
    this.currentSortHeader = value;
    this.settings.currentSortHeader = value;
    this.adminStorageService.setDocumentsSetting(this.settings);
  }

  clearSorting() {
    this.currentSort = '';
    this.currentSortHeader = '';
    this.params.sort = '';
    this.params.pageNumber = 0;
    this.settings.currentSort = '';
    this.settings.currentSortHeader = '';
    this.settings.paginationParams = this.params;
    this.adminStorageService.setDocumentsSetting(this.settings);
    this.getClearSorting++;
    this.loadVouchers();
  }

  clearSearch() {
    this.search = '';
    this.params.query = '';
    this.params.pageNumber = 0;
    this.settings.search = '';
    this.settings.paginationParams = this.params;
    this.adminStorageService.setDocumentsSetting(this.settings);
    this.loadVouchers();
  }

  searching() {
    if (this.search.length > 2 || this.search.length === 0) {
      this.params.pageNumber = 0;
      this.params.query = this.search;
      this.settings.search = this.search;
      this.settings.paginationParams = this.params;
      this.adminStorageService.setDocumentsSetting(this.settings);
      this.loadVouchers();
    }
  }

  setPagination(pagination: Pagination<any>) {
    this.pagination = pagination;
    if (this.params.pageSize != this.pagination.page.size) {
      this.params.pageSize = this.pagination.page.size;
      this.params.pageNumber = 0;
    } else {
      this.params.pageNumber = this.pagination.page.number;
    }
    this.settings.paginationParams = this.params;
    this.adminStorageService.setDocumentsSetting(this.settings);
    this.loadVouchers();
  }

  protected readonly Icons = Icons;
}
